import { jwtDecode } from "jwt-decode";
import { Location } from "@angular/common";
import { Component, OnInit, inject } from "@angular/core";
import { MyZpaxService } from "src/app/services/myzpax.service";
import { isExcludedStateRoute, isReactAppRoute } from "src/app/common/utils/myzpax-routes.util";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { sortBy } from "lodash";
import { NgxSpinnerModule, NgxSpinnerService } from "ngx-spinner";
import { firstValueFrom } from "rxjs";
import { SessionFacility } from "src/app/common/interfaces/facility-with-timezone";
import { FacilityListItem } from "src/app/common/interfaces/facility.interface";
import { FacilitiesClient } from "src/app/common/services/facility.client";
import { SecurityGroupClient } from "src/app/common/services/security-group.client";
import { StorageService } from "src/app/common/services/storage.service";
import { UserEsignatureClient } from "src/app/common/services/user-esignature.client";
import { UserClient } from "src/app/common/services/user.client";
import { AuthService } from "src/app/services/auth.service";
import { EsignatureAttestationComponent } from "../login/esignature-attestation/esignature-attestation.component";
import { CredentialsUpdate } from "src/app/common/interfaces/credentials-updates.interface";
import { CredentialUpdatesComponent } from "../login/credential-updates/credential-updates.component";
import { ConfirmationModalComponent } from "src/app/common/components/core/modal/confirmation-modal/confirmation-modal.component";
import { ConfirmIdentityComponent } from "../practitioner-setup/confirm-identity.component";
import { environment } from "environment";
import { AppConstants } from "src/app/common/constants/app-constants";
import { appRoute } from "src/app/app-route.constants";
import { User } from "src/app/common/interfaces/user.interface";
import { PageSenseService } from "src/app/common/services/page-sense.service";
import { GrowthBookService } from "src/app/services/growthbook.service";
import {
  homeUrl,
  reactHandoffIsLooping,
  reactWillAcceptSession,
  recordReactHandoff,
} from "src/app/common/utils/home-route.util";
import {
  isAuthorizationCodeSpent,
  recordAuthorizationCodeSpent,
} from "src/app/common/utils/auth-code.util";
@Component({
  selector: "app-temp-home",
  standalone: true,
  imports: [NgxSpinnerModule],
  providers: [],
  template: ` <ngx-spinner bdColor="rgba(255,255,255,0.7)" size="medium" color="#4663AE" type="square-jelly-box" [fullScreen]="true" [disableAnimation]="false"></ngx-spinner> `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        position: relative;
      }
    `,
  ],
})
export class ValidateComponent implements OnInit {
  private _spinner = inject(NgxSpinnerService);
  private _router = inject(Router);
  private _storageService = inject(StorageService);
  private _facilitiesClient = inject(FacilitiesClient);
  private _userClient = inject(UserClient);
  private _activatedRoute = inject(ActivatedRoute);
  private _securityGroupClient = inject(SecurityGroupClient);
  private _esignatureClient = inject(UserEsignatureClient);
  private _matDialog = inject(MatDialog);
  private _authService = inject(AuthService);
  private _gbService = inject(GrowthBookService);
  private _myzpaxService = inject(MyZpaxService);
  private _location = inject(Location);
  async ngOnInit() {
    this._spinner.show();
    try {
      const code = this._activatedRoute.snapshot.queryParamMap.get("code");

      // A code this tab has already redeemed means this document is running a
      // second time (see `auth-code.util`). The run that spent the code left a
      // working session behind, so continue that one — redeeming the code again
      // only fails, and treating the failure as "not logged in" starts the
      // login loop this guard exists to prevent.
      if (!code || isAuthorizationCodeSpent(code)) {
        const existingUser = this.establishedSession();
        if (!existingUser) {
          this.redirectAfterAuthFailure();
          return;
        }
        this._spinner.hide();
        this.resumeSession(existingUser, false);
        return;
      }

      recordAuthorizationCodeSpent(code);
      this.stripAuthorizationCodeFromUrl();

      let response;
      try {
        response = await firstValueFrom(this._authService.getTokenFromAuthorizationCode(code));
        this._storageService.setSessionActive();
        const decoded = jwtDecode<{ UserId?: string }>(response.access_token);
        if (decoded.UserId) {
          this._storageService.setSsoUserId(decoded.UserId);
        }
      } catch (err) {
        this.redirectAfterAuthFailure();
        return;
      }
      this._storageService.setLoginData(response, true);
      
      let isUserActive = await firstValueFrom(this._authService.IsUserActive());
      let userDetails = await firstValueFrom(this._authService.getUserInfo());
      if (!isUserActive || (!userDetails.issuperuser && userDetails.status !== AppConstants.enabled)) {
        const message = "You do not have enough privileges to access this application.Please contact your administrator or write to support@z-pax.com for assistance.";
        const title = "Access Denied";

        const val = await firstValueFrom(
          this._matDialog
            .open(ConfirmationModalComponent, {
              data: {
                title: title,
                message: message,
                buttonText: "OK",
                hideCancelButton: true,
                isError: true,
              },
              disableClose: true,
              autoFocus: false,
            })
            .afterClosed()
        );

        this._authService.logout();
        return;
      }
      userDetails = {
        ...userDetails,
        fullname: userDetails.lastname + ", " + userDetails.firstname,
      };
      this._storageService.setUser(userDetails);
      this.setGrowthBookAttributes(userDetails);
      this.setUserIdToMatomo(userDetails);
      PageSenseService.trackUser({userId : `${userDetails.fullname} - ${userDetails.id}`, name : userDetails.fullname || `${userDetails.lastname}, ${userDetails.firstname}`});
      if (userDetails.issuperuser) {
        if (!!userDetails.latestSelectedCorpId && !!userDetails.latestSelectedFacilityId) {
          this._storageService.setSelectedFacilityId(userDetails.latestSelectedFacilityId);
          this._storageService.setSelectedCorporationId(userDetails.latestSelectedCorpId);
        }
      }

      const eSignatureRequired = await firstValueFrom(this._esignatureClient.isEsignatureRequired());
      const isEsignatureRequired: boolean = eSignatureRequired.isEsignatureRequired;
      const isEsignEnabled: boolean = eSignatureRequired.isEsignEnabled;

      const isPhysician: boolean = eSignatureRequired.isPhysician;

      const securityGroupId = JSON.parse(this._storageService.getUser()!).defaultsecuritygroupid;

      const securityGroup = await firstValueFrom(this._securityGroupClient.getSecurityGroup(securityGroupId));

      const user = JSON.parse(this._storageService.getUser()!);
      if (user.npi) {
        if (!isEsignEnabled) {
          const message = "Your eSignature is yet to be authorised please contact Admin or wait for sometime.";
          const title = "Access Denied";

          const val = await firstValueFrom(
            this._matDialog
              .open(ConfirmationModalComponent, {
                data: {
                  title: title,
                  message: message,
                  buttonText: "OK",
                  hideCancelButton: true,
                  isError: true,
                },
                disableClose: true,
                autoFocus: false,
              })
              .afterClosed()
          );

          this._authService.logout();
          return;
        }
        await firstValueFrom(
          this._matDialog
            .open(ConfirmIdentityComponent, {
              disableClose: true,
              autoFocus: false,
            })
            .afterClosed()
        );
      }

      if (eSignatureRequired && securityGroup && securityGroup.name && isEsignatureRequired) {
        await firstValueFrom(
          this._matDialog
            .open(EsignatureAttestationComponent, {
              disableClose: true,
              autoFocus: false,
              data: {
                isPhysician: isPhysician,
              },
            })
            .afterClosed()
        );
      }

      if (user?.npi) {
        const npiChecks = (await firstValueFrom(this._userClient.userNPICheck(user.npi))) as CredentialsUpdate;

        if (npiChecks.differences.length > 0 && npiChecks.isUpdateFound && !npiChecks?.isNpiDeactivated) {
          const valid = await firstValueFrom(
            this._matDialog
              .open(CredentialUpdatesComponent, {
                disableClose: true,
                autoFocus: false,
                data: {
                  updates: npiChecks,
                },
              })
              .afterClosed()
          );

          if (!valid) {
            // const message = await firstValueFrom(
            //   this._esignatureClient.declineNPIData()
            // );
            // has removed message coming form backend due to message not being correct
            const message = "Please be advised that your record is not matching on our NPI records. Do contact the administrator for resolution.";
            await firstValueFrom(
              this._matDialog
                .open(ConfirmationModalComponent, {
                  data: {
                    title: "Error",
                    message: message,
                    buttonText: "OK",
                    hideCancelButton: true,
                    isError: true,
                  },
                })
                .afterClosed()
            );
            this._authService.logout();
            return;
          } else {
            sessionStorage.setItem("last-active-url", "/user/" + user.id + "/account");
            sessionStorage.setItem("last-active-user-id", user.id);
            await firstValueFrom(this._esignatureClient.updateNPIData());
            sessionStorage.clear();
            sessionStorage.clear();
            window.location.href = `${environment.zpaxEsignatureDocumentRedirectEndPoint}`;
            return;
          }
        } else if (npiChecks?.isNpiDeactivated) {
          const message = "Please be advised that your record is not available on our NPI records. Do contact the administrator for resolution.";
          await firstValueFrom(
            this._matDialog
              .open(ConfirmationModalComponent, {
                data: {
                  title: "Error",
                  message: message,
                  buttonText: "OK",
                  hideCancelButton: true,
                  isError: true,
                },
              })
              .afterClosed()
          );
          this._authService.logout();
          return;
        } else {
          //valid user redirect to zpax
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = `${environment.zpaxEsignatureDocumentRedirectEndPoint}`;
          return;
        }
      }
      this._storageService.setSelectedCorporationId(userDetails.latestSelectedCorpId ?? userDetails.defaultcorporationid);
      await this.populateFacilityList();
      const userFacilities = await firstValueFrom(this._userClient.getUserFacilities(user.id));

      const defaultFacilityIdFromList = userFacilities.find((c) => c.isdefaultfacility)?.facilityid;

      const facilityId = defaultFacilityIdFromList ?? (userFacilities.length > 0 ? userFacilities[0].facilityid : null);

      if (facilityId) {
        this._storageService.setSelectedFacilityId(facilityId);

        this._storageService.setSecurityFeatureData(await firstValueFrom(this._securityGroupClient.getFeaturePermission(facilityId)));
      } else {
        this._storageService.setSecurityFeatureData(await firstValueFrom(this._securityGroupClient.getFeaturePermission()));
      }
      this._spinner.hide();
      this.resumeSession(user, true);
    } catch (err: unknown) {
      this._spinner.hide();
      if ((err as { error: string })?.error.startsWith("The account associated with User ID")) {
      }
    }
  }

  /**
   * The session this document established, or `null` if there is not one. Used
   * to tell a replayed `/validate` (session already in place, carry on) from a
   * genuinely unauthenticated arrival such as the post-logout redirect, which
   * comes back here with no code after `logout()` has cleared storage.
   */
  private establishedSession(): User | null {
    const raw = this._storageService.getUser();
    if (!raw || sessionStorage.getItem(this._storageService.TOKEN) === null) {
      return null;
    }
    try {
      return JSON.parse(raw) as User;
    } catch {
      return null;
    }
  }

  /**
   * Takes `code` out of the address bar so a reload of this document — Safari
   * issues one whenever it restores `/validate` from the back/forward cache —
   * cannot submit it again. Other query params (`state`) are preserved.
   */
  private stripAuthorizationCodeFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    params.delete("code");
    this._location.replaceState(`/${appRoute.validate}`, params.toString());
  }

  /**
   * Sends the user wherever this session should pick up: the route they were on
   * before re-authenticating, or Home. `recordHandoff` is false when resuming a
   * session that is already running, so a replayed `/validate` does not count a
   * second bounce against the React handoff circuit breaker.
   */
  private resumeSession(user: User, recordHandoff: boolean): void {
    if (this._myzpaxService.isInMyzpax) {
      const state = this._activatedRoute.snapshot.queryParamMap.get("state");
      this.resumeAt(state && !isExcludedStateRoute(state) ? state : homeUrl(), recordHandoff);
      return;
    }

    if (this._storageService.getLastActiveUserId() !== user.id) {
      this._storageService.clearSessionState();
    }
    const lastActiveUrl = this._storageService.getLastActiveUrl();
    const activePath =
      !!lastActiveUrl && !lastActiveUrl.includes(appRoute.validate) ? lastActiveUrl : homeUrl();
    this.resumeAt(activePath, recordHandoff);
  }

  /**
   * Resume the session at `path`, which may live in either app — the React
   * routes are outside the Angular router, so they need a full-page load.
   * `last-active-url` is now written by both shells, so it can be a `/rc` path.
   */
  private resumeAt(path: string, recordHandoff = true): void {
    if (isReactAppRoute(path)) {
      // Backstop for any cycle that re-enters `/validate` on a loop: every lap
      // arrives here with a freshly minted login, so this is the one place that
      // can stop feeding it. Three logins inside the window is not something a
      // real user does, so keep them in Angular where a page will actually
      // render.
      if (!reactWillAcceptSession() || reactHandoffIsLooping()) {
        this._router
          .navigate([appRoute.home], { replaceUrl: true })
          .then(() => this._storageService.setLoggedIn());
        return;
      }
      if (recordHandoff) {
        recordReactHandoff();
      }
      this._storageService.setLoggedIn();
      // `replace`, not `href`: it drops `/validate` from history, so neither the
      // back button nor a bfcache restore can return to a page whose only job
      // was to spend an authorization code.
      window.location.replace(path);
      return;
    }
    this._router.navigateByUrl(path, { replaceUrl: true }).then(() => {
      this._storageService.setLoggedIn();
    });
  }

  private redirectAfterAuthFailure(): void {
    if (this._myzpaxService.isInMyzpax) {
      const state = this._activatedRoute.snapshot.queryParamMap.get("state");
      if (state && !isExcludedStateRoute(state)) {
        if (isReactAppRoute(state)) {
          window.location.href = state;
        } else {
          this._router.navigateByUrl(state, { replaceUrl: true });
        }
        return;
      }
    }
    // Deliberately the Angular home route rather than `navigateHome()`: this
    // runs only when there is no session (no `code`, or the token exchange
    // failed). React answers a missing session by hitting SSO *logout* and
    // returning here, which fails again — an endless login loop. Angular's
    // `userValidatedGuard` instead drives the SSO *login* flow, which is what
    // an unauthenticated user actually needs.
    this._router.navigate([appRoute.home]);
  }

  async populateFacilityList() {
    const fullFacilities = (await firstValueFrom(this._facilitiesClient.getFacilities({ length: 500000 }))).data;

    const facilitiesObject: SessionFacility[] = [];

    fullFacilities.forEach((facility: FacilityListItem) => {
      facilitiesObject.push({
        name: facility.facilityname,
        facilityId: facility.id,
        timezone: facility.timezonename || "America/New_York",
        providerNumber: facility.providerno,
      });
    });

    this._storageService.setFacilities(sortBy(facilitiesObject, "name"));
  }

  private setUserIdToMatomo(userDetails: User) {
    const userName = `${userDetails.fullname} - ${userDetails.id}`;
    if ((window as any)._paq) {
      (window as any)._paq.push(['setUserId', userName]);

      (window as any)._paq.push([
        'trackEvent',
        'Log In',
        'User Login',
        `${userName}`
      ]);
    }
  }

  private setGrowthBookAttributes(userDetails: User){
    this._gbService.setAttributes({
      id: userDetails.id,
      isSuperUser: userDetails.issuperuser,
      role: userDetails.defaultsecuritygroupid,
      facilityId: userDetails.latestSelectedFacilityId,
    });
    this._gbService.userReady$.next(true);
  }
}
